// Currency formatting helpers — honor each row's currency_code so USD shows $ and INR shows ₹
const SYMBOL = { USD: "$", GBP: "£", INR: "₹" };

export function fmtCur(n, code = "INR", decimals) {
  const sym = SYMBOL[(code || "INR").toUpperCase()] || (code || "");
  const locale = (code || "").toUpperCase() === "INR" ? "en-IN" : "en-US";
  const v = Number(n || 0);
  const dec = decimals != null ? decimals : 2;
  return sym + v.toLocaleString(locale, { minimumFractionDigits: dec, maximumFractionDigits: dec });
}

// Smart-precision rate formatter: 2dp default, but 4dp if value has >2 significant decimals.
export function fmtRate(n, code = "INR") {
  const v = Number(n || 0);
  const frac = (Math.abs(v).toString().split(".")[1] || "").replace(/0+$/, "");
  const dec = frac.length > 2 ? 4 : 2;
  return fmtCur(v, code, dec);
}

// Compact currency: USD/GBP use international scale (K/M/B), INR uses Indian scale (L/Cr).
// e.g. $5.57M, ₹2.06Cr — matches how an Indian MIS owner reads USD (millions) vs INR (crores).
export function fmtCurCompact(n, code = "INR") {
  const c = (code || "INR").toUpperCase();
  const sym = SYMBOL[c] || (code || "");
  const v = Number(n || 0);
  const locale = c === "INR" ? "en-IN" : "en-US";
  const abs = Math.abs(v);
  // keep small amounts exact-ish; compact only kicks in past 1e4/1e5 which is what we want
  const body = v.toLocaleString(locale, { notation: "compact", maximumFractionDigits: abs >= 1000 ? 2 : 0 });
  return sym + body;
}

export function fmtNum(n) {
  return Number(n || 0).toLocaleString("en-IN");
}

// Currency-aware AMOUNT (number only, no symbol) with 2 decimals: USD/GBP use Western grouping
// (3,997,126.53) while INR uses Indian grouping (39,97,126.53). Use for monetary values that show
// the currency code separately (so no symbol is prefixed).
export function fmtAmount(n, code = "INR") {
  const locale = (code || "INR").toUpperCase() === "INR" ? "en-IN" : "en-US";
  return Number(n || 0).toLocaleString(locale, { minimumFractionDigits: 2, maximumFractionDigits: 2 });
}

const _MONTHS = ["Jan", "Feb", "Mar", "Apr", "May", "Jun",
                 "Jul", "Aug", "Sep", "Oct", "Nov", "Dec"];

// Parse a date value into {y,m,d} WITHOUT any timezone conversion. The backend stores dates
// as timezone-less midnight ISO strings (e.g. "2026-07-20T00:00:00"); converting via UTC
// (toISOString) shifts IST midnight back a day, so we read the calendar date verbatim.
export function ymdParts(v) {
  if (!v) return null;
  if (typeof v === "string") {
    const m = v.match(/^(\d{4})-(\d{2})-(\d{2})/);
    if (m) return { y: +m[1], m: +m[2], d: +m[3] };
  }
  const dt = new Date(v);
  if (isNaN(dt.getTime())) return null;
  return { y: dt.getFullYear(), m: dt.getMonth() + 1, d: dt.getDate() };
}

// App-wide date display: DD-MMM-YY (e.g. "20-Jul-26"). Timezone-safe.
export function ddMmmYy(v) {
  const p = ymdParts(v);
  if (!p) return "";
  return `${String(p.d).padStart(2, "0")}-${_MONTHS[p.m - 1]}-${String(p.y).slice(-2)}`;
}

export function fmtDate(d) {
  return d ? (ddMmmYy(d) || "—") : "—";
}

// Timestamp display: DD-MMM-YY, HH:MM (keeps time for audit fields like sync/reconcile/created).
export function fmtDateTime(v) {
  if (!v) return "—";
  const disp = ddMmmYy(v);
  if (!disp) return "—";
  const dt = new Date(v);
  if (isNaN(dt.getTime())) return disp;
  const hh = String(dt.getHours()).padStart(2, "0");
  const mm = String(dt.getMinutes()).padStart(2, "0");
  return `${disp}, ${hh}:${mm}`;
}
